/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * PdfGenStudyDetails — View/edit PdfGen study details + acknowledgement document templates.
 * Migrated from: lego/page/eos/transfer-media/PdfGenStudyDetails.jsx
 */

import { useState, useEffect } from 'react'
import { useServiceContext } from '../../../root'
import Modal from '../../../components/Modal'
import AcknowledgementDocSelection from './acknowledgement-doc-selection/AcknowledgementDocSelection'
import type {
  PdfGenStudyDetailsProps,
  FtpContext,
  SelectedDocTypes,
  StudyGroupOption,
  StudyOption,
  EnvironmentOption
} from './types'

const PdfGenStudyDetails: React.FC<PdfGenStudyDetailsProps> = ({ ftpId, isReview, isPdrFlow }) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const [isOpen, setIsOpen] = useState(true)
  const [ftpConfig, setFtpConfig] = useState<Partial<FtpContext>>({})
  const [studyGroup, setStudyGroup] = useState<Partial<StudyGroupOption>>({})
  const [studyData, setStudyData] = useState<Partial<StudyOption>>({})
  const [environmentData, setEnvironmentData] = useState<Partial<EnvironmentOption>>({})
  const [ackDocTemplateSelection, setAckDocTemplateSelection] = useState<Partial<SelectedDocTypes>>({})

  useEffect(() => {
    Promise.all([context.service.eosService!.loadStudyInfo(ftpId, null), context.api.settings.getFtpContext(ftpId)])
      .then((response: [unknown, unknown]) => {
        const ftpConfigData = response[1] as FtpContext
        setFtpConfig(ftpConfigData)
        getStudyInfoData(response[0] as { studyGroup: string; study: string; environment: string })
      })
      .catch((error: { data?: { message?: string } }) => {
        context.service.toast.error(error?.data?.message ?? 'ERROR')
        doClose()
      })
  }, [ftpId])

  const getStudyInfoData = (studyInfoResponse: { studyGroup: string; study: string; environment: string }) => {
    context.service.eosService!.getStudyGroups().then((_data) => {
      const data = _data as StudyGroupOption[]
      const studyGroupData = data.find((element) => element.uuid === studyInfoResponse.studyGroup)
      if (!studyGroupData) return
      setStudyGroup(studyGroupData)

      context.api.eos.getStudyGroup(studyGroupData.uuid, studyGroupData.mccEnabled ?? false).then((_studiesData) => {
        const studiesData = _studiesData as { elements: StudyOption[] }
        const studyInfoData = studiesData.elements.find((element) => element.uuid === studyInfoResponse.study)
        if (studyInfoData) {
          setStudyData(studyInfoData)
        }
        if (studyGroupData.mccEnabled && studyInfoData) {
          context.api.eos.getPlinthStudyEnvironments(studyInfoData.uuid).then((_envData) => {
            const envData = _envData as EnvironmentOption[]
            const environment = envData.find((element) => element.uuid === studyInfoResponse.environment)
            if (environment) {
              setEnvironmentData(environment)
            }
          })
        }
      })
    })
  }

  const handleAckDocUpdate = (data: SelectedDocTypes) => {
    setAckDocTemplateSelection(data)
  }

  const saveDetails = () => {
    if (
      ackDocTemplateSelection?.siteAckTemplateFsiId &&
      ackDocTemplateSelection?.sponsorAckTemplateFsiId &&
      !isReview
    ) {
      const data = {
        eosTemplateMapDTO: {
          eosStudyId: ackDocTemplateSelection.eosStudyId ?? 0,
          siteAckTemplateFsiId: ackDocTemplateSelection.siteAckTemplateFsiId,
          siteAckTemplateFsiName: ackDocTemplateSelection.siteAckTemplateFsiName ?? '',
          sponsorAckTemplateFsiId: ackDocTemplateSelection.sponsorAckTemplateFsiId,
          sponsorAckTemplateFsiName: ackDocTemplateSelection.sponsorAckTemplateFsiName ?? ''
        }
      }
      context.api.eos.updatePdfGenStudyData(data, ftpId).then(
        () => {
          context.service.toast.success('EOS_SETUP_STUDY_GROUP_SUCCESS_MSG')
          doClose()
        },
        (error: { data?: { message?: string } }) => {
          context.service.toast.error(error?.data?.message ?? 'ERROR')
          doClose()
        }
      )
    } else {
      doClose()
    }
  }

  const doClose = () => {
    setIsOpen(false)
  }

  const titleText = isReview
    ? translate(isPdrFlow ? 'RIGHT_TOP_NAV_EOS_PDR' : 'EOS_SETUP_USERS_WORKFLOWS_STEP1_CONFIRMATION_TITLE') +
      ' - ' +
      translate('EOS_TRANSFER_MEDIA_STUDY_DETAILS_VIEW')
    : translate('EOS_SETUP_USERS_WORKFLOWS_STEP1_CONFIRMATION_TITLE') +
      ' - ' +
      translate('EOS_TRANSFER_MEDIA_STUDY_DETAILS_EDIT')

  return (
    <Modal
      show={isOpen}
      handleClose={doClose}
      title={titleText}
      isLevelTwoModal
      className="study-info-dialog level-one-modal"
    >
      <Modal.Body>
        <div className="study-name-wrapper flex-container field-wrapper align-items-start">
          <label className="control-label half-width">{translate('EMTF_MD_FILTER_TRIAL_NAME')}</label>
          <div className="half-width slide-left"> {ftpConfig?.name} </div>
        </div>
        <div className="study-name-wrapper flex-container field-wrapper align-items-start">
          <label className="control-label half-width">{translate('EOS_NEW_STUDY_FOLDER_PATH_LABEL')}</label>
          <div className="half-width slide-left word-break"> {ftpConfig?.remoteFolder} </div>
        </div>
        <div className="study-name-wrapper flex-container field-wrapper align-items-start">
          <label className="control-label half-width">
            {translate(isPdrFlow ? 'FTP_FORM_LABEL_PDR_DESTINATION_FOLDER' : 'FTP_FORM_LABEL_RCM_DESTINATION_FOLDER')}
          </label>
          <div className="half-width slide-left"> {ftpConfig?.targetFolderName} </div>
        </div>
        <div className="study-name-wrapper flex-container field-wrapper align-items-start">
          <label className="control-label half-width">{translate('EOS_SETUP_STUDY_GROUP')}</label>
          <div className="half-width slide-left"> {studyGroup?.name} </div>
        </div>
        <div className="study-name-wrapper flex-container field-wrapper align-items-start">
          <label className="control-label half-width">{translate('EOS_SETUP_STUDY')}</label>
          <div className="half-width slide-left"> {studyData?.name} </div>
        </div>
        <div className="study-name-wrapper flex-container field-wrapper align-items-start">
          <label className="control-label half-width">{translate('EMTF_MD_ENVIRONMENT')}</label>
          <div className="half-width slide-left"> {environmentData?.name} </div>
        </div>
        {!isPdrFlow && (
          <div className="study-name-wrapper flex-container field-wrapper align-items-start">
            <AcknowledgementDocSelection
              handleAckDocUpdate={handleAckDocUpdate}
              contextId={ftpConfig?.id ?? undefined}
              isReview={isReview}
            />
          </div>
        )}
        {isPdrFlow && (
          <>
            <div className="study-name-wrapper flex-container field-wrapper align-items-start">
              <label className="control-label half-width">{translate('CONTENT_TYPE')}</label>
              <div className="half-width slide-left"> {ftpConfig?.contentType} </div>
            </div>
            <div className="study-name-wrapper flex-container field-wrapper align-items-start">
              <label className="control-label half-width">{translate('DATA_TYPE')}</label>
              <div className="half-width slide-left"> {ftpConfig?.dataType} </div>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Button variant="link" onClick={doClose}>
          {translate('CANCEL')}
        </Modal.Footer.Button>
        {!isPdrFlow && (
          <Modal.Footer.Button variant="primary" onClick={saveDetails}>
            {translate(isReview ? 'OK' : 'SAVE')}
          </Modal.Footer.Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}

export default PdfGenStudyDetails
